import { capitalizeFirstLetter } from "utils/common";

import type { XentryJob, XentryJobValue } from "modules/AppointmentDetails/components/CarDetails/components/XentryStatus/queryKeys";

export enum XENTRY_ROW_TYPE {
  Section = 1,
  Value
}

type XentryRow = {
  id: string;
  type: XENTRY_ROW_TYPE;
  label: string;
  level: number;
  value?: string;
};

const EMPTY_VALUE = "-";
const MAX_INDENT_LEVEL = 5;

const isNestedObject = (value: XentryJobValue): value is XentryJob => typeof value === "object" && value !== null && !Array.isArray(value);
const formatValue = (value: XentryJobValue) => (value === null || value === "" ? EMPTY_VALUE : String(value));

export const buildXentryRows = (job: XentryJob, level = 0, parentId = ""): XentryRow[] =>
  Object.entries(job).reduce<XentryRow[]>((rows, [key, value]) => {
    const id = `${parentId}${key}`;
    const label = capitalizeFirstLetter(key);
    const indentLevel = Math.min(level, MAX_INDENT_LEVEL);

    if (isNestedObject(value)) {
      rows.push({ id, type: XENTRY_ROW_TYPE.Section, label, level: indentLevel });
      rows.push(...buildXentryRows(value, level + 1, `${id}.`));
      return rows;
    }

    if (Array.isArray(value)) {
      rows.push({ id, type: XENTRY_ROW_TYPE.Section, label, level: indentLevel });

      if (!value.length) rows.push({ id: `${id}.empty`, type: XENTRY_ROW_TYPE.Value, label, level: indentLevel + 1, value: EMPTY_VALUE });

      value.forEach((entry, index) => {
        const entryId = `${id}.${index}`;
        const entryLabel = `${label} ${index + 1}`;

        if (isNestedObject(entry)) {
          rows.push({ id: entryId, type: XENTRY_ROW_TYPE.Section, label: entryLabel, level: Math.min(level + 1, MAX_INDENT_LEVEL) });
          rows.push(...buildXentryRows(entry, level + 2, `${entryId}.`));
          return;
        }

        rows.push({ id: entryId, type: XENTRY_ROW_TYPE.Value, label: entryLabel, level: Math.min(level + 1, MAX_INDENT_LEVEL), value: formatValue(entry) });
      });

      return rows;
    }

    rows.push({ id, type: XENTRY_ROW_TYPE.Value, label, level: indentLevel, value: formatValue(value) });
    return rows;
  }, []);
